/**
 * The optimistic overlay: what the clinician just did on My week, shown before
 * the server confirms it. Pure functions over `LocalOverlay` and a snapshot; the
 * reducer calls `reconcileOverlay` on every `SERVER_SNAPSHOT`.
 *
 * An entry is satisfied (and dropped) when the snapshot agrees with it: a moved
 * visit reads at the overlaid clinician and start; a canceled visit reads
 * canceled; an added visit or block exists for the same clinician, start and
 * patient / title; a removed block reads canceled or has gone; a **re-timed
 * block** reads at its new start. Entries older than the TTL are dropped and
 * reported.
 */
import { LOCAL_OVERLAY_TTL_MS, type LocalOverlay, type ServerSnapshot } from "@caredna-marlin/utils/homeHealthDemo/state";

export type OverlayKey = `visit:${number}` | `added:${string}` | `block:${string}` | `cancelBlock:${number}` | `moveBlock:${number}`;

export function overlayKeys(local: LocalOverlay): OverlayKey[] {
  return [
    ...Object.keys(local.visits).map((id) => `visit:${Number(id)}` as OverlayKey),
    ...local.addedVisits.map((v) => `added:${v.tempId}` as OverlayKey),
    ...local.blocks.map((b) => `block:${b.tempId}` as OverlayKey),
    ...Object.keys(local.canceledBlocks ?? {}).map((id) => `cancelBlock:${Number(id)}` as OverlayKey),
    ...Object.keys(local.movedBlocks ?? {}).map((id) => `moveBlock:${Number(id)}` as OverlayKey),
  ];
}

export function isOverlayEmpty(local: LocalOverlay): boolean {
  return (
    Object.keys(local.visits).length === 0 &&
    local.addedVisits.length === 0 &&
    local.blocks.length === 0 &&
    Object.keys(local.canceledBlocks ?? {}).length === 0 &&
    Object.keys(local.movedBlocks ?? {}).length === 0
  );
}

export function removeOverlayKeys(local: LocalOverlay, keys: string[]): LocalOverlay {
  if (keys.length === 0) return local;
  const drop = new Set(keys);
  const visits = Object.fromEntries(Object.entries(local.visits).filter(([id]) => !drop.has(`visit:${Number(id)}`)));
  const addedVisits = local.addedVisits.filter((v) => !drop.has(`added:${v.tempId}`));
  const blocks = local.blocks.filter((b) => !drop.has(`block:${b.tempId}`));
  const canceledBlocks = Object.fromEntries(Object.entries(local.canceledBlocks ?? {}).filter(([id]) => !drop.has(`cancelBlock:${Number(id)}`)));
  const movedBlocks = Object.fromEntries(Object.entries(local.movedBlocks ?? {}).filter(([id]) => !drop.has(`moveBlock:${Number(id)}`)));
  return { visits, addedVisits, blocks, canceledBlocks, movedBlocks };
}

export interface ReconcileResult {
  local: LocalOverlay;
  satisfied: OverlayKey[];
  expired: OverlayKey[];
}

function sameInstant(a: string, b: string): boolean {
  return new Date(a).getTime() === new Date(b).getTime();
}

export function reconcileOverlay(local: LocalOverlay, snapshot: ServerSnapshot, now: Date, ttlMs: number = LOCAL_OVERLAY_TTL_MS): ReconcileResult {
  if (isOverlayEmpty(local)) return { local, satisfied: [], expired: [] };
  const satisfied: OverlayKey[] = [];
  const expired: OverlayKey[] = [];
  const byVisit = new Map<number, ServerSnapshot["visitResources"][number]>();
  for (const vr of snapshot.visitResources) if (!byVisit.has(vr.visitId)) byVisit.set(vr.visitId, vr);
  const isExpired = (appliedAtIso: string) => now.getTime() - new Date(appliedAtIso).getTime() > ttlMs;

  for (const [id, change] of Object.entries(local.visits)) {
    const key = `visit:${Number(id)}` as OverlayKey;
    const vr = byVisit.get(change.visitId);
    const agrees = vr
      ? change.canceled
        ? vr.eventStatus === "canceled"
        : sameInstant(vr.startIso, change.startIso) && (change.providerId === undefined || vr.providerId === change.providerId)
      : false;
    if (agrees) satisfied.push(key);
    else if (isExpired(change.appliedAtIso)) expired.push(key);
  }
  for (const added of local.addedVisits) {
    const key = `added:${added.tempId}` as OverlayKey;
    const exists = snapshot.visitResources.some(
      (vr) => vr.providerId === added.providerId && sameInstant(vr.startIso, added.startIso) && vr.eventStatus !== "canceled" && (added.patientId !== undefined ? vr.patientId === added.patientId : vr.patientName === added.patientName),
    );
    if (exists) satisfied.push(key);
    else if (isExpired(added.appliedAtIso)) expired.push(key);
  }
  // A removed block is satisfied when the snapshot reads it canceled — or no
  // longer carries it at all, which is what a horizon that filters canceled
  // blocks would show.
  for (const [id, removed] of Object.entries(local.canceledBlocks ?? {})) {
    const key = `cancelBlock:${Number(id)}` as OverlayKey;
    const block = snapshot.blocks.find((b) => b.id === removed.blockId);
    if (!block || block.canceled) satisfied.push(key);
    else if (isExpired(removed.appliedAtIso)) expired.push(key);
  }
  // A re-timed block is satisfied when the snapshot reads it at the new start.
  // A block the snapshot has stopped carrying at all — canceled elsewhere — has
  // nothing left to be optimistic about, so its entry goes too.
  for (const [id, moved] of Object.entries(local.movedBlocks ?? {})) {
    const key = `moveBlock:${Number(id)}` as OverlayKey;
    const block = snapshot.blocks.find((b) => b.id === moved.blockId);
    if (!block || sameInstant(block.startIso, moved.startIso)) satisfied.push(key);
    else if (isExpired(moved.appliedAtIso)) expired.push(key);
  }
  for (const block of local.blocks) {
    const key = `block:${block.tempId}` as OverlayKey;
    const exists = snapshot.blocks.some((b) => !b.canceled && b.providerId === block.providerId && sameInstant(b.startIso, block.startIso) && (b.title ?? "") === block.title);
    if (exists) satisfied.push(key);
    else if (isExpired(block.appliedAtIso)) expired.push(key);
  }
  const drop = [...satisfied, ...expired];
  return { local: drop.length ? removeOverlayKeys(local, drop) : local, satisfied, expired };
}
